'use client';

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { HorizontalSeparator } from '@/app_components/ui/Separator';
import { cn } from '@/app_lib/utils/utils';

interface CarouselProps {
  children?: ReactNode;
  className?: string;
  displayNum?: number;
  hideSeparator?: boolean;
}

export const Carousel = ({ children, className, displayNum = 4, hideSeparator = false }: CarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  const scrollTo = (direction: 'left' | 'right') => {
    const container = scrollRef.current;

    if (!container) {
      return;
    }

    const cardWidth = container.firstElementChild?.clientWidth || 0;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;

    container.scrollBy({ left: scrollAmount });
  };

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setIsLeftDisabled(scrollLeft === 0);
    setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth - 1); // -1 for subpixel rendering
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  // NOTE: We need to pre-compute these in JS because Tailwind generates its css at build time
  const mdCols = `calc((100% - ${displayNum - 2} * var(--gap)) / ${displayNum - 1})`;
  const lgCols = `calc((100% - ${displayNum - 1} * var(--gap)) / ${displayNum})`;

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <div
        className='
          grid grid-flow-col
          overflow-x-auto md:overflow-x-hidden
          scrollbar-hidden scroll-smooth
          snap-x snap-mandatory
          gap-4 md:gap-8 [--gap:theme(spacing.8)]
          auto-cols-[75%]
          md:auto-cols-[var(--md-cols)] 
          lg:auto-cols-[var(--lg-cols)]
          -mx-4 md:mx-0
          pr-4 md:pr-0
        '
        ref={scrollRef}
        style={{
          ['--md-cols' as any]: mdCols,
          ['--lg-cols' as any]: lgCols,
        }}
      >
        {children}
      </div>
      <div className='flex flex-col gap-lg'>
        <div className='hidden md:flex ml-auto gap-lg'>
          <ActionableIcon
            className='p-2 border rounded-full stroke-helper bg-secondary'
            disabled={isLeftDisabled}
            onClick={() => scrollTo('left')}
          >
            <Icon icon='ChevronLeft' />
          </ActionableIcon>
          <ActionableIcon
            className='p-2 border rounded-full stroke-helper bg-secondary'
            disabled={isRightDisabled}
            onClick={() => scrollTo('right')}
          >
            <Icon icon='ChevronRight' />
          </ActionableIcon>
        </div>
        {!hideSeparator && <HorizontalSeparator className='stroke-helper' />}
      </div>
    </div>
  );
};
